/**
 * AiChatActionContext - provides a stable ref to the latest `onAction` handler.
 *
 * Solves the stale-closure problem where the `rightRailItems` memo in DealPage
 * may capture an old `handleChatAction` reference (before the questionnaire has
 * loaded and `dealPageFieldLookup` is populated). Instead of threading the prop
 * through a memo that might not re-run, the parent registers the handler here and
 * `AiStructuredMessage` reads the always-current ref as a fallback.
 *
 * Usage:
 *   // Provider (e.g. DealPageContent):
 *   const chatActionRef = React.useRef<AiMessageActionHandler | undefined>(undefined);
 *   chatActionRef.current = handleChatAction;
 *   return <AiChatActionProvider handlerRef={chatActionRef}>...</AiChatActionProvider>;
 *
 *   // Consumer (AiStructuredMessage / ActionFooter):
 *   const chatActionRef = useAiChatActionRef();
 *   const handler = onAction ?? chatActionRef?.current;
 */

import type { AiMessageActionHandler } from '@protoss/shared';
import * as React from 'react';

type ActionHandlerRef = React.MutableRefObject<AiMessageActionHandler | undefined>;

const AiChatActionContext = React.createContext<ActionHandlerRef | null>(null);

export function AiChatActionProvider({
  children,
  handlerRef,
}: {
  children: React.ReactNode;
  handlerRef: ActionHandlerRef;
}) {
  return <AiChatActionContext.Provider value={handlerRef}>{children}</AiChatActionContext.Provider>;
}

/** Returns the stable ref object. Read `.current` at call-time to get the latest handler. */
export function useAiChatActionRef(): ActionHandlerRef | null {
  return React.useContext(AiChatActionContext);
}
